import React, { useMemo } from 'react';
import {
  experimental_extendTheme as materialExtendTheme,
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useTranslation } from 'react-i18next';
import { zhCN, enUS, arSD } from '@mui/x-data-grid/locales';
import { resolveAppLanguage } from '../../../i18n';

function materialDirectionForLanguage(i18nLanguage) {
  return resolveAppLanguage(i18nLanguage) === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Emotion-injected `sx` (e.g. Sheet + sticky columns) beats plain App.css.
 * In RTL, unstick only *middle* columns so first (e.g. checkbox) + last (e.g. actions) can stay sticky.
 */
const rtlTableUnstickStyles = {
  'html[dir="rtl"] .MuiDataGrid-columnHeaders, html[dir="rtl"] .MuiDataGrid-columnHeadersInner, html[dir="rtl"] .MuiDataGrid-columnHeader':
    {
      position: 'relative !important',
      top: 'unset !important',
      bottom: 'unset !important',
      zIndex: 'unset !important',
    },
  'html[dir="rtl"] .MuiDataGrid-pinnedColumnHeaders, html[dir="rtl"] .MuiDataGrid-pinnedColumns--left, html[dir="rtl"] .MuiDataGrid-pinnedColumns--right':
    {
      position: 'relative !important',
    },
  // Middle columns only: first/last keep sticky (customer tables + Joy sticky header corners)
  'html[dir="rtl"] table.MuiTable-stickyHeader thead th:not(:first-of-type):not(:last-of-type), html[dir="rtl"] table.MuiTable-stickyHeader thead .MuiTableCell-root:not(:first-of-type):not(:last-of-type)':
    {
      position: 'static !important',
      top: 'unset !important',
      zIndex: 'unset !important',
    },
  'html[dir="rtl"] table.MuiTable-stickyHeader thead tr:nth-of-type(2) th': {
    top: 'unset !important',
  },
  'html[dir="rtl"] table.MuiTable-stickyFooter tfoot th, html[dir="rtl"] table.MuiTable-stickyFooter tfoot td': {
    position: 'static !important',
    bottom: 'unset !important',
    zIndex: 'unset !important',
  },
  'html[dir="rtl"] thead th:not(:first-of-type):not(:last-of-type), html[dir="rtl"] thead .MuiTableCell-head:not(:first-of-type):not(:last-of-type)': {
    position: 'static !important',
    top: 'unset !important',
    left: 'unset !important',
    right: 'unset !important',
    zIndex: 'unset !important',
    boxShadow: 'none !important',
    textAlign: 'start !important',
  },
  'html[dir="rtl"] thead th:first-of-type, html[dir="rtl"] thead .MuiTableCell-head:first-of-type': {
    textAlign: 'start !important',
  },
  'html[dir="rtl"] thead th:last-of-type, html[dir="rtl"] thead .MuiTableCell-head:last-of-type': {
    textAlign: 'start !important',
  },
  'html[dir="rtl"] tbody td:not(:first-of-type):not(:last-of-type), html[dir="rtl"] tbody .MuiTableCell-body:not(:first-of-type):not(:last-of-type)': {
    position: 'static !important',
    left: 'unset !important',
    right: 'unset !important',
    zIndex: 'unset !important',
    boxShadow: 'none !important',
    textAlign: 'start !important',
  },
  'html[dir="rtl"] tbody td:first-of-type, html[dir="rtl"] tbody .MuiTableCell-body:first-of-type': {
    textAlign: 'start !important',
  },
  'html[dir="rtl"] tbody td:last-of-type, html[dir="rtl"] tbody .MuiTableCell-body:last-of-type': {
    textAlign: 'start !important',
  },
};

/**
 * Joy UI Button uses physical marginLeft/marginRight on decorators; in RTL that
 * misaligns icon + label padding. Use logical margins and mirrored icon offsets.
 */
const rtlJoyButtonDecoratorStyles = {
  'html[dir="rtl"] .MuiButton-startDecorator': {
    marginRight: 0,
    marginInlineEnd: 'var(--Button-gap)',
    '--Icon-margin': '0 calc(var(--Button-gap) / -2) 0 0',
    '--CircularProgress-margin': '0 calc(var(--Button-gap) / -2) 0 0',
  },
  'html[dir="rtl"] .MuiButton-endDecorator': {
    marginLeft: 0,
    marginInlineStart: 'var(--Button-gap)',
    '--Icon-margin': '0 0 0 calc(var(--Button-gap) / -2)',
    '--CircularProgress-margin': '0 0 0 calc(var(--Button-gap) / -2)',
  },
};

/** Joy ModalClose uses physical `right`; mirror to the visual start side in RTL (top-left). */
const rtlJoyModalCloseStyles = {
  'html[dir="rtl"] .MuiModalClose-root': {
    right: 'unset',
    insetInlineEnd: 'var(--ModalClose-inset, 0.625rem)',
  },
};

const rtlArabicGlobalStyles = {
  ...rtlTableUnstickStyles,
  ...rtlJoyButtonDecoratorStyles,
  ...rtlJoyModalCloseStyles,
};

/**
 * Sets MUI DataGrid defaultProps.localeText from @mui/x-data-grid/locales
 * (includes TablePagination strings such as “Rows per page”) based on app language.
 */
export function MaterialThemeWithDataGridLocale({ children }) {
  const { i18n } = useTranslation();
  const localePack =
    i18n.language?.startsWith('zh') ? zhCN : i18n.language?.startsWith('ar') ? arSD : enUS;
  const direction = materialDirectionForLanguage(i18n.language);
  const theme = useMemo(
    () =>
      materialExtendTheme({
        direction,
        components: {
          MuiDataGrid: {
            defaultProps: {
              localeText: localePack.components.MuiDataGrid.defaultProps.localeText,
            },
          },
        },
      }),
    [localePack, direction]
  );

  const isAr = resolveAppLanguage(i18n.language) === 'ar';

  return (
    <MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: theme }}>
      {isAr ? <GlobalStyles styles={rtlArabicGlobalStyles} /> : null}
      {children}
    </MaterialCssVarsProvider>
  );
}
